import { useEffect, useRef, useCallback, useState } from 'react';
import type { GamePhase } from '../types/kotch';

// Import audio files
import menuMusic from '../components/Kotch/Kotch.mp3';
import battleMusic from '../components/Kotch/kotchBattleMusic.mp3';
import ambience from '../components/Kotch/ambience.mp3';

// ═══════════════════════════════════════════════════════════════
// KOTCH - AUDIO MANAGEMENT HOOK
// ═══════════════════════════════════════════════════════════════

// Relative volume levels (these will be multiplied by master volume)
const RELATIVE_VOLUMES = {
  music: 1.0,        // Main game music - full relative volume
  ambience: 0.25,    // Ambient sounds - subtle background
};

// Default master volume (10%)
const DEFAULT_MASTER_VOLUME = 0.10;

// Phases that use menu music vs battle music
const MENU_PHASES: GamePhase[] = ['setup'];

export function useKotchAudio(phase: GamePhase) {
  // Master volume state (default 10%)
  const [masterVolume, setMasterVolume] = useState(DEFAULT_MASTER_VOLUME);
  const [isMuted, setIsMuted] = useState(false);

  // Audio element refs
  const menuMusicRef = useRef<HTMLAudioElement | null>(null);
  const battleMusicRef = useRef<HTMLAudioElement | null>(null);
  const ambienceRef = useRef<HTMLAudioElement | null>(null);
  const currentTrackRef = useRef<'menu' | 'battle' | null>(null);

  // Calculate actual volume for a track
  const getVolume = useCallback((relativeVolume: number) => {
    if (isMuted) return 0;
    return masterVolume * relativeVolume;
  }, [masterVolume, isMuted]);

  // Update all audio element volumes
  const updateVolumes = useCallback(() => {
    if (menuMusicRef.current) {
      menuMusicRef.current.volume = getVolume(RELATIVE_VOLUMES.music);
    }
    if (battleMusicRef.current) {
      battleMusicRef.current.volume = getVolume(RELATIVE_VOLUMES.music);
    }
    if (ambienceRef.current) {
      ambienceRef.current.volume = getVolume(RELATIVE_VOLUMES.ambience);
    }
  }, [getVolume]);

  // Initialize audio elements
  const initializeAudio = useCallback(() => {
    if (!menuMusicRef.current) {
      menuMusicRef.current = new Audio(menuMusic);
      menuMusicRef.current.loop = true;
      menuMusicRef.current.volume = getVolume(RELATIVE_VOLUMES.music);
    }

    if (!battleMusicRef.current) {
      battleMusicRef.current = new Audio(battleMusic);
      battleMusicRef.current.loop = true;
      battleMusicRef.current.volume = getVolume(RELATIVE_VOLUMES.music);
    }

    if (!ambienceRef.current) {
      ambienceRef.current = new Audio(ambience);
      ambienceRef.current.loop = true;
      ambienceRef.current.volume = getVolume(RELATIVE_VOLUMES.ambience);
    }
  }, [getVolume]);

  // Update volumes when master volume or mute state changes
  useEffect(() => {
    updateVolumes();
  }, [updateVolumes]);

  // Switch between menu and battle music based on phase
  useEffect(() => {
    initializeAudio();

    const isMenu = MENU_PHASES.includes(phase);
    const desiredTrack = isMenu ? 'menu' : 'battle';

    // Start ambience if not playing
    if (ambienceRef.current?.paused !== false) {
      ambienceRef.current?.play().catch(() => {});
    }

    // Switch music track if needed
    if (currentTrackRef.current !== desiredTrack) {
      const outgoing = desiredTrack === 'battle' ? menuMusicRef.current : battleMusicRef.current;
      const incoming = desiredTrack === 'battle' ? battleMusicRef.current : menuMusicRef.current;

      if (outgoing) {
        outgoing.pause();
        outgoing.currentTime = 0;
      }
      if (incoming) {
        incoming.currentTime = 0;
        incoming.play().catch(() => {});
      }
      currentTrackRef.current = desiredTrack;
    } else if (
      (desiredTrack === 'menu' && menuMusicRef.current?.paused !== false) ||
      (desiredTrack === 'battle' && battleMusicRef.current?.paused !== false)
    ) {
      // Music should be playing but isn't (e.g. first render)
      const track = desiredTrack === 'menu' ? menuMusicRef.current : battleMusicRef.current;
      track?.play().catch(() => {});
      currentTrackRef.current = desiredTrack;
    }
  }, [phase, initializeAudio]);

  // Stop all audio
  const stopGameAudio = useCallback(() => {
    if (menuMusicRef.current) {
      menuMusicRef.current.pause();
      menuMusicRef.current.currentTime = 0;
    }
    if (battleMusicRef.current) {
      battleMusicRef.current.pause();
      battleMusicRef.current.currentTime = 0;
    }
    if (ambienceRef.current) {
      ambienceRef.current.pause();
      ambienceRef.current.currentTime = 0;
    }
    currentTrackRef.current = null;
  }, []);

  // Toggle mute
  const toggleMute = useCallback(() => {
    setIsMuted(prev => !prev);
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopGameAudio();
      menuMusicRef.current = null;
      battleMusicRef.current = null;
      ambienceRef.current = null;
    };
  }, [stopGameAudio]);

  return {
    masterVolume,
    setMasterVolume,
    isMuted,
    toggleMute,
    startGameAudio: () => {}, // kept for interface compat
    stopGameAudio,
  };
}

export default useKotchAudio;
